/**
 * Nexus Pricing Configuration
 *
 * Business-plan adapter that exposes Nexus execution data from the
 * centralized Losi monetization config.
 *
 * Primary responsibilities:
 * - Expose business plan tiers and pricing to legacy Nexus surfaces
 * - Centralize feature definitions and limits
 * - Provide plan validation and feature checking
 *
 * Dependencies:
 * - `lib/billing/losi-pricing.ts`
 */

import { LOSI_BUSINESS_PLANS } from '@/lib/billing/losi-pricing'

// Nexus Plan Constants
export const NEXUS_PLAN_STARTER = "nexus_starter"
export const NEXUS_PLAN_PROFESSIONAL = "nexus_professional"
export const NEXUS_PLAN_ENTERPRISE = "nexus_enterprise"

export const NEXUS_PLAN_STARTER_MONTHLY = "nexus_starter_monthly"
export const NEXUS_PLAN_STARTER_YEARLY = "nexus_starter_yearly"
export const NEXUS_PLAN_PROFESSIONAL_MONTHLY = "nexus_professional_monthly"
export const NEXUS_PLAN_PROFESSIONAL_YEARLY = "nexus_professional_yearly"
export const NEXUS_PLAN_ENTERPRISE_MONTHLY = "nexus_enterprise_monthly"
export const NEXUS_PLAN_ENTERPRISE_YEARLY = "nexus_enterprise_yearly"

// Nexus Pricing Configuration
const BUSINESS_STARTER_PLAN = LOSI_BUSINESS_PLANS.business_starter
const BUSINESS_PRO_PLAN = LOSI_BUSINESS_PLANS.business_pro
const BUSINESS_ULTRA_PLAN = LOSI_BUSINESS_PLANS.business_ultra

export const NEXUS_PRICING = {
  starter: {
    monthly: BUSINESS_STARTER_PLAN.monthlyPriceUsd,
    yearly: BUSINESS_STARTER_PLAN.yearlyPriceUsd!,
    yearlyMonthly: BUSINESS_STARTER_PLAN.yearlyMonthlyPriceUsd!,
    name: BUSINESS_STARTER_PLAN.publicName,
    description: BUSINESS_STARTER_PLAN.description,
    targetUsers: "Small teams",
    callsPerMonth: BUSINESS_STARTER_PLAN.includedCredits,
    integrations: 10,
    industryTemplates: "Basic templates",
    analytics: "Basic",
    support: "Email",
    aiReceptionists: BUSINESS_STARTER_PLAN.includedAgents,
    includedProSeats: BUSINESS_STARTER_PLAN.includedProSeats,
    overageRateUsd: BUSINESS_STARTER_PLAN.overageRateUsd,
    multiAgent: false,
    customWorkflows: false
  },
  professional: {
    monthly: BUSINESS_PRO_PLAN.monthlyPriceUsd,
    yearly: BUSINESS_PRO_PLAN.yearlyPriceUsd!,
    yearlyMonthly: BUSINESS_PRO_PLAN.yearlyMonthlyPriceUsd!,
    name: BUSINESS_PRO_PLAN.publicName,
    description: BUSINESS_PRO_PLAN.description,
    targetUsers: "Growing teams",
    callsPerMonth: BUSINESS_PRO_PLAN.includedCredits,
    integrations: 30,
    industryTemplates: "4 Verticals",
    analytics: "Advanced",
    support: "Priority",
    aiReceptionists: BUSINESS_PRO_PLAN.includedAgents,
    includedProSeats: BUSINESS_PRO_PLAN.includedProSeats,
    overageRateUsd: BUSINESS_PRO_PLAN.overageRateUsd,
    multiAgent: true,
    customWorkflows: true
  },
  enterprise: {
    monthly: BUSINESS_ULTRA_PLAN.monthlyPriceUsd,
    yearly: BUSINESS_ULTRA_PLAN.yearlyPriceUsd!,
    yearlyMonthly: BUSINESS_ULTRA_PLAN.yearlyMonthlyPriceUsd!,
    name: BUSINESS_ULTRA_PLAN.publicName,
    description: BUSINESS_ULTRA_PLAN.description,
    targetUsers: "High-volume teams",
    callsPerMonth: BUSINESS_ULTRA_PLAN.includedCredits,
    integrations: 50,
    industryTemplates: "All Verticals",
    analytics: "Advanced",
    support: "Dedicated",
    aiReceptionists: BUSINESS_ULTRA_PLAN.includedAgents,
    includedProSeats: BUSINESS_ULTRA_PLAN.includedProSeats,
    overageRateUsd: BUSINESS_ULTRA_PLAN.overageRateUsd,
    multiAgent: true,
    customWorkflows: true,
    customAgents: true,
    whiteLabel: true,
    dedicatedManager: true
  }
} as const

// Nexus Feature Categories
export const NEXUS_FEATURES = {
  core: [
    {
      name: "AI Agents",
      starter: "1 AI agent",
      professional: "Up to 5 AI agents",
      enterprise: "Up to 10 AI agents"
    },
    {
      name: "Monthly Credits",
      starter: "500 credits/month",
      professional: "2,000 credits/month",
      enterprise: "10,000 credits/month"
    },
    {
      name: "Integrations",
      starter: "10+ integrations",
      professional: "30+ integrations",
      enterprise: "50+ integrations"
    },
    {
      name: "Industry Templates",
      starter: "Basic templates",
      professional: "4 industry verticals",
      enterprise: "All industry verticals"
    }
  ],
  advanced: [
    {
      name: "Analytics & Reporting",
      starter: "Basic analytics",
      professional: "Advanced analytics & reporting",
      enterprise: "Advanced analytics & reporting"
    },
    {
      name: "Multi-Agent Orchestration",
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: "Custom Workflows",
      starter: false,
      professional: true,
      enterprise: true
    },
    {
      name: "Custom AI Agents",
      starter: false,
      professional: false,
      enterprise: true
    },
    {
      name: "White-Label Options",
      starter: false,
      professional: false,
      enterprise: true
    }
  ],
  support: [
    {
      name: "Support Level",
      starter: "Email support",
      professional: "Priority support",
      enterprise: "Dedicated success manager"
    },
    {
      name: "Response Time",
      starter: "Business hours",
      professional: "Priority response",
      enterprise: "24/7 dedicated support"
    }
  ],
  compliance: [
    {
      name: "Security Standards",
      starter: true,
      professional: true,
      enterprise: true
    },
    {
      name: "HIPAA Compliance",
      starter: "Available",
      professional: "Available",
      enterprise: "Included"
    },
    {
      name: "PCI DSS",
      starter: "Available",
      professional: "Available",
      enterprise: "Included"
    },
    {
      name: "SOC 2",
      starter: "Available",
      professional: "Available",
      enterprise: "Included"
    }
  ]
} as const

// Nexus Industry Templates
export const NEXUS_INDUSTRY_TEMPLATES = {
  starter: [
    "General Business",
    "Basic Healthcare",
    "Retail & Hospitality"
  ],
  professional: [
    "Dental Practices",
    "Medical Spas",
    "HVAC Services",
    "Plumbing Services",
    "Restaurants",
    "Customer Care Centers"
  ],
  enterprise: [
    "All Professional Templates",
    "Legal Firms",
    "Real Estate",
    "Insurance",
    "Financial Services",
    "Manufacturing",
    "Construction",
    "Education",
    "Government",
    "Non-Profit"
  ]
} as const

// Nexus Integration Categories
export const NEXUS_INTEGRATIONS = {
  crm: [
    "Salesforce",
    "HubSpot",
    "Zoho CRM",
    "Pipedrive",
    "Monday.com"
  ],
  calendar: [
    "Google Calendar",
    "Outlook Calendar",
    "Calendly",
    "Acuity Scheduling"
  ],
  communication: [
    "Twilio",
    "Slack",
    "Microsoft Teams",
    "Discord",
    "WhatsApp Business"
  ],
  payment: [
    "Stripe",
    "PayPal",
    "Square",
    "QuickBooks",
    "Xero"
  ],
  healthcare: [
    "Epic FHIR",
    "Cerner",
    "PracticeLink",
    "Dentrix"
  ],
  hospitality: [
    "Toast POS",
    "Square POS",
    "OpenTable",
    "Resy"
  ]
} as const

// Helper Functions
export function getNexusPlanFromStripe(plan: string) {
  // Handle both old format ('starter') and new format ('nexus_starter')
  if (plan.startsWith('nexus_')) {
    const planPrefix = plan.split('_')[0] + '_' + plan.split('_')[1]
    switch (planPrefix) {
      case 'nexus_starter':
        return NEXUS_PLAN_STARTER
      case 'nexus_professional':
        return NEXUS_PLAN_PROFESSIONAL
      case 'nexus_enterprise':
        return NEXUS_PLAN_ENTERPRISE
      default:
        return null
    }
  } else {
    // Handle old format without 'nexus_' prefix
    switch (plan) {
      case 'starter':
        return NEXUS_PLAN_STARTER
      case 'professional':
        return NEXUS_PLAN_PROFESSIONAL
      case 'enterprise':
        return NEXUS_PLAN_ENTERPRISE
      default:
        return null
    }
  }
}

export function isNexusPlan(plan: string) {
  return plan.startsWith('nexus_')
}

export function getNexusPlanLimits(plan: string) {
  const nexusPlan = getNexusPlanFromStripe(plan)
  if (!nexusPlan) return null

  switch (nexusPlan) {
    case NEXUS_PLAN_STARTER:
      return NEXUS_PRICING.starter
    case NEXUS_PLAN_PROFESSIONAL:
      return NEXUS_PRICING.professional
    case NEXUS_PLAN_ENTERPRISE:
      return NEXUS_PRICING.enterprise
    default:
      return null
  }
}

export function getNexusPlanFeatures(plan: string) {
  const nexusPlan = getNexusPlanFromStripe(plan)
  if (!nexusPlan) return null

  const baseFeatures = NEXUS_FEATURES.core
  const advancedFeatures = NEXUS_FEATURES.advanced
  const supportFeatures = NEXUS_FEATURES.support
  const complianceFeatures = NEXUS_FEATURES.compliance

  return {
    core: baseFeatures,
    advanced: advancedFeatures,
    support: supportFeatures,
    compliance: complianceFeatures
  }
}
